import { DialogDescription } from '@radix-ui/react-dialog';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { type SyntheticEvent, useCallback, useEffect, useRef, useState } from 'react';
import type { FieldValues, SubmitHandler } from 'react-hook-form';
import { v4 as uuidv4 } from 'uuid';
import { courseIdIsOnMap } from '@/components/CourseMap/course-utils';
import type { CourseMapRoot } from '@/components/CourseMap/coursemap.types';
import EditCoursePanel from '@/components/CreateNewCoursePanel/CreateNewCoursePanel';
import Loader from '@/components/Loader';
import { showErrorToast } from '@/components/ToastNotification';
import { Button } from '@/components/ui/button';
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
	DialogTrigger,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { updateCourseMap } from '@/hooks/mutations/courseMapMutations';
import { useGetAdminPanelCourses } from '@/hooks/queries/useAdminPanelCourses';
import { useGetDisciplines } from '@/hooks/queries/useDisciplines';
import { useGetDivisions } from '@/hooks/queries/useDivisions';
import { EditIcon, SearchIcon, TrashCanIcon } from '@/icons/index';
import type { CourseWithRelations, Discipline } from '@/lib/types';
import { StyledSearch } from '@/Shared/StyledElements';
import { StyledH4 } from '@/Shared/Typography/typography';
import { StyledCourseList, StyledListItem } from './CourseMapList.Styles';
import CourseMapListForm from './CourseMapListForm';

interface CourseMapListProps {
	newCourse: any;
	selectedNode: any;
	treeData: any;
	updateTreeData(obj: any, label?: string): void;
	deleteCourse(a: CourseWithRelations): void;
	checkTreeDiff(a: FieldValues): void;
}

const CourseMapList = ({
	newCourse,
	selectedNode,
	treeData,
	updateTreeData,
	deleteCourse,
	checkTreeDiff,
}: CourseMapListProps) => {
	const [courseOptions, setCourseOptions] = useState<CourseWithRelations[]>();
	const [resultQty, setResultQty] = useState<number>(1);
	const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
	const [editingCourse, setEditingCourse] = useState<CourseWithRelations | undefined | null>();
	const [isSuccess, setIsSuccess] = useState(false);
	const ref = useRef(courseOptions);
	const searchInput = useRef(document.createElement('input'));
	const { data: coursesList, isLoading } = useGetAdminPanelCourses();
	const { data: disciplines } = useGetDisciplines();
	const { data: divisions } = useGetDivisions();
	const disciplineDivisionList = { disciplines: disciplines ?? [], divisions: divisions ?? [] };
	const queryClient = useQueryClient();
	const filterCourses = (e: SyntheticEvent) => {
		e.persist();
		const filterValue = (e.currentTarget as HTMLInputElement).value.toLowerCase();

		const list = [...(coursesList ?? [])].filter((course: any) =>
			course.course_name.toLowerCase().includes(filterValue)
		);

		if (filterValue === '') {
			setCourseOptions(ref.current);
		} else {
			setCourseOptions(list);
		}
		setResultQty(list?.length);
	};

	function isTypeDiscipline(type: any): type is Discipline {
		if ((type as Discipline).color) {
			return true;
		}
		return false;
	}

	const chooseOption = (node: any) => {
		// ?? better way of preventing multiple additions
		const activeIcon = document.querySelector('.active-icon');
		if (activeIcon === null || (node as Course)?.is_archived) return;
		if (isTypeDiscipline(node)) {
			newCourse.id = node.id;
			newCourse.tracker = node.id;
			newCourse.data.name = node.name;
			newCourse.data.id = node.id;
			newCourse.data.tracker = node.id;
			newCourse.data.division = '';
			newCourse.data.discipline = node.name;
			newCourse.data.color = node.color;
			newCourse.data.isDiscipline = true;
		} else {
			newCourse.id = node.course_id;
			newCourse.tracker = node.course_id;
			newCourse.data.name = node.course_name;
			newCourse.data.id = node.course_id;
			newCourse.data.tracker = node.course_id;
			newCourse.data.division = node?.courses_division?.division.name;
			newCourse.data.discipline = node?.courses_discipline?.discipline.name;
		}

		if (selectedNode.children) {
			selectedNode.children.push(newCourse);
		} else {
			selectedNode.children = [newCourse];
		}

		if (selectedNode.data.children) {
			selectedNode.data.children.push(newCourse.data);
		} else {
			selectedNode.data.children = [newCourse.data];
		}

		const kind = isTypeDiscipline(node) ? 'Discipline' : 'Course';
		updateTreeData(treeData, `Added ${kind} '${newCourse.data.name}'`);
	};

	const schoolId = import.meta.env.VITE_SCHOOL_ID;

	const { mutate: courseMutation, status } = useMutation({
		mutationFn: updateCourseMap,
		mutationKey: ['course-map-mutation'],
		onSuccess: (course) => {
			setIsSuccess(true);
			setTimeout(() => {
				setIsSuccess(false);
				setEditingCourse(null);
			}, 2000);

			const previousCourseData = queryClient.getQueryData(['get-admin-panel-courses', schoolId]) as
				| CourseWithRelations[]
				| undefined;

			if (previousCourseData) {
				const elementsIdx = previousCourseData.findIndex(
					(element: any) => element.course_id === course.course_id
				);
				if (elementsIdx !== -1) {
					// Create a new array instead of mutating in place
					const updatedCourseData = [...previousCourseData];
					updatedCourseData[elementsIdx] = course as any;
					queryClient.setQueryData(['get-admin-panel-courses', schoolId], updatedCourseData);
				}
			}
		},
		onError: () => {
			showErrorToast('Oh no, something went wrong... Please try again.');
		},
	});

	const onSubmit: SubmitHandler<FieldValues> = (formFields) => {
		const { disciplineEdits, divisionEdits, courseNameEdits, grade, isAp, isArchived } = formFields;
		// currently disciplineId doesn't exist with course map object
		// use discipline name to get reference to discipline object
		const disciplineId =
			disciplineDivisionList?.disciplines?.find((discipline) => discipline.name === disciplineEdits)
				?.id ?? '';

		const divisionId =
			disciplineDivisionList?.divisions?.find((division) => division.name === divisionEdits)?.id ??
			'';

		if (editingCourse?.course_id) {
			courseMutation({
				disciplineId,
				divisionId,
				courseName: courseNameEdits,
				courseId: editingCourse.course_id,
				grade,
				isAp,
				isArchived,
			});
			// Only sync map JSON + review drawer when this course is placed on the map.
			if (courseIdIsOnMap(treeData as CourseMapRoot, editingCourse.course_id)) {
				checkTreeDiff({ ...formFields, courseId: editingCourse.course_id });
			}
		}
	};

	const handleEditCourse = useCallback((course: CourseWithRelations) => {
		setEditingCourse({ ...course });
	}, []);

	useEffect(() => {
		searchInput?.current?.focus();
	}, []);

	useEffect(() => {
		const abortController = new AbortController();

		ref.current = coursesList;
		setCourseOptions(coursesList);
		return () => {
			abortController.abort();
		};
	}, [coursesList]);

	const archivedCourseList = [...(courseOptions ?? [])]?.filter((course) => course.is_archived);

	return (
		<StyledCourseList>
			{isLoading && <Loader size="small" />}
			{!isLoading && (
				<>
					{selectedNode?.depth === 0 && (
						<>
							<StyledH4 mb="var(--spacing-4)">Add Discipline</StyledH4>
							<div className="selection-container discipline-container">
								{disciplineDivisionList?.disciplines.map((discipline) => {
									const { id, name } = discipline;
									return (
										<StyledListItem key={`$panel-${id}`}>
											<div className="item-row">
												<div className="button-group">
													<button
														className="course-button"
														type="button"
														onClick={() => chooseOption(discipline)}
														data-id={id}
													>
														{name}
													</button>
												</div>
											</div>
										</StyledListItem>
									);
								})}
							</div>
						</>
					)}
					{selectedNode?.depth !== 0 && (
						<>
							<StyledSearch className="mb-2 mx-0!">
								<SearchIcon classList="absolute left-3 top-1/2 -translate-y-1/2" />
								<Input
									ref={searchInput}
									placeholder="Search..."
									type="text"
									onInput={(e) => filterCourses(e)}
								/>
							</StyledSearch>
							<Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
								<DialogTrigger asChild>
									<Button className="mb-4 w-fit">Add New Course</Button>
								</DialogTrigger>
								<DialogContent className="max-w-[400px]">
									<DialogDescription className="sr-only">
										Add a new course to the course map.
									</DialogDescription>
									<DialogHeader>
										<DialogTitle className="text-md">Add New Course</DialogTitle>
									</DialogHeader>
									{disciplineDivisionList?.disciplines && (
										<EditCoursePanel
											disciplines={disciplineDivisionList.disciplines}
											divisions={disciplineDivisionList.divisions}
											closeAccordion={() => setIsCreateDialogOpen(false)}
											onCourseCreated={(course) => {
												chooseOption(course as any);
												setIsCreateDialogOpen(false);
											}}
										/>
									)}
								</DialogContent>
							</Dialog>
							<Tabs defaultValue="courses">
								<TabsList variant="line" fillWidth className="w-full">
									<TabsTrigger variant="line" value="courses">
										Courses
									</TabsTrigger>
									<TabsTrigger variant="line" value="archived-courses">
										Archived Courses
									</TabsTrigger>
								</TabsList>
								<TabsContent value="courses">
									<div className="selection-container">
										{[
											{
												course_name: '- Spacer -',
												course_id: `${uuidv4()}`,
											} as Partial<CourseWithRelations>,
											...(courseOptions ?? []),
										]
											?.filter((course) => !(course as CourseWithRelations).is_archived)
											?.map((course) => {
												const { course_id: courseId, course_name: courseName } = course;

												const isSpacerBtn = courseName?.includes('Spacer');
												const classList = isSpacerBtn
													? 'course-button spacer-button'
													: 'course-button';

												return (
													<StyledListItem key={courseId}>
														<div className="item-row">
															<div className="button-group">
																<button
																	className={classList}
																	disabled={editingCourse?.course_id === courseId}
																	type="button"
																	onClick={() => chooseOption(course as any)}
																	data-id={courseId}
																>
																	{courseName}
																</button>
																{!isSpacerBtn && (
																	<>
																		<EditIcon
																			disabled={editingCourse?.course_id === courseId}
																			passedEvent={() =>
																				handleEditCourse(course as CourseWithRelations)
																			}
																		/>
																		<TrashCanIcon
																			disabled={editingCourse?.course_id === courseId}
																			passedEvent={() =>
																				deleteCourse(course as CourseWithRelations)
																			}
																		/>
																	</>
																)}
															</div>
														</div>
													</StyledListItem>
												);
											})}
										{resultQty === 0 && <span className="no-matches-text">No Matches...</span>}
									</div>
								</TabsContent>
								<TabsContent value="archived-courses">
									<div className="selection-container selection-container--archive">
										{archivedCourseList?.length === 0 && resultQty !== 0 && (
											<span className="no-matches-text">No Archived Courses...</span>
										)}
										{archivedCourseList?.map((course) => {
											const { course_id: courseId, course_name: courseName } = course;

											const isSpacerBtn = courseName?.includes('Spacer');
											const classList = isSpacerBtn
												? 'course-button spacer-button'
												: 'course-button';

											return (
												<StyledListItem key={courseId}>
													<div className="item-row">
														<div className="button-group">
															<button
																data-archived={course?.is_archived}
																disabled
																className={classList}
																type="button"
																onClick={() => chooseOption(course as any)}
																data-id={courseId}
																key={courseId}
															>
																{courseName}
															</button>
															{!isSpacerBtn && (
																<>
																	<EditIcon
																		disabled={editingCourse?.course_id === courseId}
																		passedEvent={() => {
																			handleEditCourse(course);
																		}}
																	/>
																	<TrashCanIcon
																		disabled={editingCourse?.course_id === courseId}
																		passedEvent={() => deleteCourse(course)}
																	/>
																</>
															)}
														</div>
													</div>
												</StyledListItem>
											);
										})}
										{resultQty === 0 && <span className="no-matches-text">No Matches...</span>}
									</div>
								</TabsContent>
							</Tabs>
							<Dialog
								open={!!editingCourse?.course_id}
								onOpenChange={(open) => {
									if (!open) setEditingCourse(null);
								}}
							>
								<DialogContent className="max-w-[400px]">
									<DialogDescription className="sr-only">
										Edit the course details.
									</DialogDescription>
									<DialogHeader>
										<DialogTitle className="text-md">
											Editing: {editingCourse?.course_name}
										</DialogTitle>
									</DialogHeader>
									{editingCourse?.course_id && (
										<CourseMapListForm
											key={editingCourse.course_id}
											onSubmit={onSubmit}
											editingCourse={editingCourse}
											isSuccess={isSuccess}
											saveStatus={status}
											setEditingCourse={setEditingCourse}
											disciplineList={disciplineDivisionList?.disciplines}
											divisionList={disciplineDivisionList?.divisions}
										/>
									)}
								</DialogContent>
							</Dialog>
						</>
					)}
				</>
			)}
		</StyledCourseList>
	);
};

export default CourseMapList;
